import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, X, Megaphone, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const tr = (t, key, fallback) => {
  const v = t(key);
  return !v || v === key ? fallback : v;
};

/** PRO-side panel: list active sponsorship offers + create new ones. */
export default function OffersPanel() {
  const { t } = useLanguage();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const KIND_OPTS = [
    { id: 'gim_toli', label: tr(t, 'ads.offerKindGimToli', 'Gim Toli') },
    { id: 'podcast', label: tr(t, 'ads.offerKindPodcast', 'Podcast') },
  ];

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/monetization/offers', { params: { mine: true } });
      setOffers(r.data?.offers || []);
    } catch (_e) {
      // ignore
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const remove = async (id) => {
    if (!window.confirm(tr(t, 'ads.confirmDeleteOffer', 'Supprimer cette offre ?'))) return;
    try {
      await axios.delete(`/api/monetization/offers/${id}`);
      toast.success(tr(t, 'ads.offerDeleted', 'Offre supprimée'));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr(t, 'ads.genericError', 'Erreur'));
    }
  };

  return (
    <div className="space-y-4" data-testid="pro-offers-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2"><Megaphone className="w-4 h-4 text-emerald-400" /> {tr(t, 'ads.myOffersPanel', 'Mes offres de sponsoring')}</h3>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 text-sm font-semibold flex items-center gap-1.5"
          data-testid="offer-create-btn"
        >
          <Plus className="w-4 h-4" /> {tr(t, 'ads.newOffer', 'Nouvelle offre')}
        </button>
      </div>

      {loading && <div className="flex justify-center py-6 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>}

      {!loading && offers.length === 0 && (
        <div className="p-6 text-center text-gray-500 bg-white/5 rounded-2xl border border-white/10">
          {tr(t, 'ads.noOffersYet', "Aucune offre publiée. Crée-en une pour qu'elle apparaisse aux créateurs lors du partage.")}
        </div>
      )}

      <div className="space-y-2">
        {offers.map((of) => (
          <div key={of.id} className="p-4 rounded-2xl bg-white/5 border border-white/10" data-testid={`offer-item-${of.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="font-semibold">{of.title}</div>
              <div className="flex items-center gap-2">
                <div className="text-emerald-300 font-bold whitespace-nowrap">{of.amount} {of.currency}</div>
                <button
                  type="button"
                  onClick={() => remove(of.id)}
                  className="p-1.5 rounded-full hover:bg-rose-500/20 text-rose-300"
                  data-testid={`offer-delete-${of.id}`}
                  aria-label={tr(t, 'ads.delete', 'Supprimer')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-2">{of.description}</p>
            <div className="text-xs text-gray-500">
              {tr(t, 'ads.acceptedTypes', 'Types acceptés')} : {of.content_kinds?.map((k) => KIND_OPTS.find((x) => x.id === k)?.label).filter(Boolean).join(', ')} · {tr(t, 'ads.minTalk', 'Durée parlée minimum')} : {of.min_talk_seconds}s
            </div>
          </div>
        ))}
      </div>

      {showForm && <OfferForm KIND_OPTS={KIND_OPTS} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); reload(); }} />}
    </div>
  );
}

function OfferForm({ KIND_OPTS, onClose, onCreated }) {
  const { t } = useLanguage();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kinds, setKinds] = useState(['gim_toli']);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [busy, setBusy] = useState(false);

  const toggleKind = (id) => setKinds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!title.trim()) return toast.error(tr(t, 'ads.offerTitleRequired', 'Titre requis'));
    if (!description.trim()) return toast.error(tr(t, 'ads.offerDescRequired', 'Description requise'));
    if (kinds.length === 0) return toast.error(tr(t, 'ads.chooseAtLeastOneKind', 'Choisis au moins un type de contenu'));
    if (!Number.isFinite(amt) || amt <= 0) return toast.error(tr(t, 'ads.offerAmountInvalid', 'Montant invalide'));
    setBusy(true);
    try {
      await axios.post('/api/monetization/offers', {
        title: title.trim(),
        description: description.trim(),
        content_kinds: kinds,
        amount: amt,
        currency,
      });
      toast.success(tr(t, 'ads.offerPublished', 'Offre publiée'));
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr(t, 'ads.genericError', 'Erreur'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4">
      <form onSubmit={submit} className="w-full max-w-lg max-h-[92vh] overflow-y-auto bg-gray-900 text-white rounded-t-3xl sm:rounded-3xl border border-white/10">
        <div className="sticky top-0 bg-gray-900/95 backdrop-blur-md border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold">{tr(t, 'ads.newOffer', 'Nouvelle offre')}</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10" data-testid="offer-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">{tr(t, 'ads.offerTitleLabel', 'Titre')}</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={tr(t, 'ads.offerTitlePlaceholder', 'Ex : Lancement de notre nouveau parfum')} className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl" data-testid="offer-title-input" />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">{tr(t, 'ads.offerDescLabel', 'Description (que doit dire le créateur ?)')}</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder={tr(t, 'ads.offerDescPlaceholder', 'Décris le produit ou service que le créateur doit mentionner…')} className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl resize-none" data-testid="offer-description-input" />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">{tr(t, 'ads.offerKindsLabel', 'Types de contenu acceptés')}</label>
            <div className="flex flex-wrap gap-2">
              {KIND_OPTS.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  onClick={() => toggleKind(k.id)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${kinds.includes(k.id) ? 'bg-emerald-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                  data-testid={`offer-kind-${k.id}`}
                >
                  {k.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <input type="number" min={1} step="0.01" placeholder={tr(t, 'ads.offerAmountPlaceholder', 'Montant')} value={amount} onChange={(e) => setAmount(e.target.value)} className="flex-1 px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl" data-testid="offer-amount-input" />
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl" data-testid="offer-currency-select">
              {['EUR', 'USD', 'GBP', 'CAD', 'CHF', 'AUD', 'JPY', 'CNY', 'INR', 'RUB', 'BRL', 'AED', 'MAD', 'XOF', 'KES'].map((c) => (
                <option key={c} value={c} className="bg-gray-900">{c}</option>
              ))}
            </select>
          </div>
          <div className="text-xs text-gray-500 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200">
            {tr(t, 'ads.offerRule', "Le créateur doit parler du sponsor pendant 10 secondes minimum dans son Gim Toli ou podcast (ou simple mention sur film court). Cette règle est imposée par Destine.")}
          </div>
          <button type="submit" disabled={busy} className="w-full py-3 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 font-bold disabled:opacity-50" data-testid="offer-submit-btn">
            {busy ? tr(t, 'ads.publishing', 'Publication…') : tr(t, 'ads.publishOffer', "Publier l'offre")}
          </button>
        </div>
      </form>
    </div>
  );
}
